/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef, useState, useEffect } from 'react'
import { css } from '@emotion/core'
import { DialogOverlayContent, IconButton } from '@fs/zion-ui'
import OpenCloseAnimation from '@fs/zion-ui/dist/es/open-close-animation/OpenCloseAnimation'
import useBodyScrollLock from '@fs/zion-ui/dist/es/use-body-scroll-lock/useBodyScrollLock'
import useFocusLock from '@fs/zion-ui/dist/es/use-focus-lock/useFocusLock'
import useAutoFocus from '@fs/zion-ui/dist/es/use-auto-focus/useAutoFocus'
import useEscHandler from '@fs/zion-ui/dist/es/use-esc-handler/useEscHandler'
import Overlay from '@fs/zion-ui/dist/es/overlay/Overlay'
import { boolAttr } from '@fs/zion-frontend-friends'
import { MenuClose } from '@fs/zion-icon'
import useIsMobile from '../hooks/useIsMobile'

const scrimCss = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  &::before {
    content: '';
    width: 100vw;
    height: 100vh;
    opacity: 0.35;
    background: black;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const overlayCss = css`
  background: #2e2e2e;
  border-radius: 12px;
  padding: 40px;
  max-width: 680px;
  width: 100vw;
  max-height: 100vh;
  overflow-y: auto;
  box-shadow: 0px 8px 36px rgba(0, 0, 0, 0.65);
  --content-padding-left: 0;
  --content-padding-right: 0;
  --content-padding-top: 0;
  --content-padding-bottom: 0;

  &[is-mobile] {
    border-radius: 0;
    height: 100vh;
  }
`
const closeButtonCss = css`
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 1;
`

const openStyles = {
  transform: 'scale(1)',
  opacity: 1,
  config: {
    tension: 300,
  },
}
const closeStyles = {
  transform: 'scale(0.85)',
  opacity: 0,
  config: {
    tension: 750,
    clamp: true,
  },
}

export default function RootsTechOverlay({
  isOpen,
  setIsOpen,
  focusBackRef,
  children,
  actions,
  mixinOverlayStyles,
  showClose,
}) {
  const keepScrollRef = useRef()
  const overlayRef = useRef()
  const [animIsOpen, setAnimIsOpen] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      setAnimIsOpen(true)
    }
  }, [isOpen])

  const handleClose = () => {
    setAnimIsOpen(false)
    setIsOpen(false)
    focusBackRef?.current?.focus()
  }

  // Lock body scrolling when openw
  useBodyScrollLock(isOpen, keepScrollRef)

  const focusLockHandler = useFocusLock({
    enabled: isOpen,
    containerRef: overlayRef,
  })

  useAutoFocus({
    isOpen,
    enabled: true,
    refToFocus: overlayRef,
  })

  // Close on escape key
  useEscHandler({
    handler: handleClose,
    enabled: isOpen,
  })
  const isMobile = useIsMobile()

  return isOpen ? (
    <div css={scrimCss} onClick={handleClose}>
      <OpenCloseAnimation
        isOpen={animIsOpen}
        openStyles={openStyles}
        closeStyles={closeStyles}
        onCloseFinished={() => {
          setIsOpen(false)
        }}
      >
        <Overlay
          ref={overlayRef}
          z={0}
          onBlur={isOpen ? focusLockHandler : undefined}
          // Stop click propagation to prevent the scrim onclick from firing
          onClick={(e) => e.stopPropagation()}
        >
          <div css={overlayCss} is-mobile={boolAttr(isMobile)} style={mixinOverlayStyles}>
            <DialogOverlayContent>
              <div ref={keepScrollRef}>{children}</div>
            </DialogOverlayContent>
            {actions}
            {isMobile || showClose ? (
              <div css={closeButtonCss}>
                <IconButton
                  Icon={MenuClose}
                  aria-label="Close"
                  data-testid="dialog-close-button"
                  onClick={handleClose}
                />
              </div>
            ) : null}
          </div>
        </Overlay>
      </OpenCloseAnimation>
    </div>
  ) : null
}
